import axios from "axios";
import Constants from "../services/Constants";
import {
  constructQueryUrl,
  formatDateWithoutTimeToIsoString,
  formatDateForUTC,
} from "./Utils";

export const randColor = () =>
  `hsl(${Math.round(Math.random() * 360)}, 70%, 50%)`;

export const getDeploymentsByUsers = async () => {
  const url = Constants.DASHBOARD_GET_DEPLOYMENTS.replace(":group", "users");
  const { data = [] } = await axios.get(url);
  const result = data.map((item) => {
    return { ...item, color: randColor() };
  });
  return result;
};

export const getDeploymentsByStatus = async () => {
  const url = Constants.DASHBOARD_GET_DEPLOYMENTS.replace(":group", "status");
  const { data = [] } = await axios.get(url);
  const result = data.map((item) => {
    return { ...item, color: randColor() };
  });
  return result;
};

export const getAnalyticsData = async ({ queryKey }) => {
  const [_, type] = queryKey;
  const url = Constants.DASHBOARD_GET_ANALYTICS.replace(":type", type);
  const { data = [] } = await axios.get(url);
  return {
    id: type,
    data,
  };
};

export const getDeploymentsCalendar = async ({ queryKey }) => {
  const [_, type, year, period] = queryKey;
  let result = [];
  if (type === "year") {
    const url = Constants.DASHBOARD_GET_DEPLOYMENT_FOR_YEAR.replace(
      ":year",
      year,
    ).replace(":group", "year");
    const { data } = await axios.get(url);
    result = data.map((item) => {
      return { day: item.id, value: parseInt(item.value) };
    });
  } else if (type === "last_365_days") {
    const url = Constants.DASHBOARD_GET_DEPLOYMENT_FOR_DATE_RANGE.replace(
      ":startdate",
      formatDateWithoutTimeToIsoString(period.from),
    ).replace(":enddate", formatDateWithoutTimeToIsoString(period.to));
    const { data } = await axios.get(url);
    result = data.map((item) => {
      return { day: item.id, value: parseInt(item.value) };
    });
  }
  return result;
};

export const getDeploymentsByPeriod = async ({ queryKey }) => {
  const [_, type, period] = queryKey;
  let url = "";
  let result = [];
  if (type === "current_year") {
    url = Constants.DASHBOARD_GET_DEPLOYMENT_FOR_YEAR.replace(
      ":year",
      period,
    ).replace(":group", "month");
    const { data = [] } = await axios.get(url);
    const monthsData = [...Constants.MONTHS_NAMES];
    data.forEach((item) => {
      const month = item?.x?.split("-")[1];
      monthsData[parseInt(month) - 1].y = item.y;
    });
    result = {
      id: "Deployments",
      data: monthsData,
    };
  } else if (type === "date_range") {
    url = Constants.DASHBOARD_GET_DEPLOYMENT_FOR_DATE_RANGE.replace(
      ":startdate",
      formatDateForUTC(period.from, "from"),
    ).replace(":enddate", formatDateForUTC(period.to, "to"));
    const { data = [] } = await axios.get(url);
    const rangeData = data.map((item) => {
      return {
        x: new Date(item.id).toLocaleDateString(),
        y: parseInt(item.value),
      };
    });
    result = {
      id: "Deployments",
      data: rangeData,
    };
  } else if (type === "last_365_days") {
    url = Constants.DASHBOARD_GET_DEPLOYMENTS.replace(":group", "365days");
    const { data = [] } = await axios.get(url);
    result = {
      id: "Deployments",
      data,
    };
  }
  return result;
};

export const getStatsForDashboard = async () => {
  const { data = {} } = await axios.get(Constants.DASHBOARD_GET_DASH_STATS);
  return data;
};

export const getUserActivityCalendar = async ({ queryKey }) => {
  const [_, type, year, period, userid] = queryKey;
  let result = [];
  if (type === "year") {
    const url = Constants.GET_USER_ACTIVITY_FOR_YEAR.replace(":type", "year")
      .replace(":userid", userid)
      .replace(":year", year);
    const { data = [] } = await axios.get(url);
    result = data.map((item) => {
      return { day: item.id, value: parseInt(item.value) };
    });
  } else if (type === "last_365_days") {
    const url = Constants.GET_USER_ACTIVITY_FOR_DATE_RANGE.replace(
      ":type",
      "range",
    )
      .replace(":userid", userid)
      .replace(":from", formatDateWithoutTimeToIsoString(period.from))
      .replace(":to", formatDateWithoutTimeToIsoString(period.to));
    const { data = [] } = await axios.get(url);
    result = data.map((item) => {
      return { day: item.id, value: parseInt(item.value) };
    });
  }
  return result;
};

export const getReportsForProvisioningByDateRange = async ({ queryKey }) => {
  const [
    _,
    pageOrObj,
    pageSize,
    field,
    sort,
    filterModel,
    columns,
    extraFilters,
    fetchAll,
  ] = queryKey;

  let page = 1;
  let take = 20;
  let sortby = field;
  let order = sort;
  let filters = filterModel;
  let cols = columns;
  let isFetchAll = fetchAll;
  let type;
  let dateRange;

  if (typeof pageOrObj === "object" && pageOrObj !== null) {
    type = pageOrObj.type;
    dateRange = pageOrObj.dateRange;
    page = pageOrObj.page || 1;
    take = pageOrObj.pageSize || 20;
    sortby = pageOrObj.sortby;
    order = pageOrObj.order;
    filters = pageOrObj.filterModel;
  } else {
    page = pageOrObj || 1;
    take = pageSize || 20;
    type = extraFilters?.type;
    dateRange = extraFilters?.dateRange;
  }

  if (!type || !dateRange?.from || !dateRange?.to) {
    return { data: { res: [], meta: { itemCount: 0, pageCount: 0 } } };
  }

  let url = Constants.REPORTS_GET_PROVISIONING_DATA_FOR_DATE_RANGE.replace(
    ":type",
    type,
  )
    .replace(":startdate", new Date(dateRange.from).toISOString())
    .replace(":enddate", new Date(dateRange.to).toISOString());

  url += `&page=${page}&take=${take}`;
  if (sortby) {
    url += `&sortby=${sortby}`;
  }
  if (order) {
    url += `&order=${order.toUpperCase()}`;
  }
  if (isFetchAll) {
    url += `&fetchAll=true`;
  }

  url = constructQueryUrl({ filterModel: filters, query: url, columns: cols });
  return await axios.get(url);
};

export const getLastSeenReport = async ({ queryKey }) => {
  const [_, dateRange] = queryKey;
  const { from, to } = dateRange;
  let url = Constants.REPORTS_GET_LASTSEEN_DATA.replace(
    ":from",
    from?.toISOString(),
  ).replace(":to", to?.toISOString());
  const { data } = await axios.get(url);
  return data;
};

export const getReportsForSchedulerByDateRangeWithPagination = async ({
  queryKey,
}) => {
  const [
    _,
    page = 1,
    pageSize = 25,
    field = "updatedAt",
    sort = "DESC",
    filterModel = {},
    columns,
    extraFilters = {},
    fetchAll,
  ] = queryKey;

  const { from, to } = extraFilters || {};

  let query =
    Constants.REPORTS_GET_SCHEDULER_DATA_FOR_DATE_RANGE +
    `?page=${page}&take=${pageSize}&sortby=${field}&order=${sort.toUpperCase()}`;

  if (from && to) {
    query += `&from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`;
  }

  if (fetchAll) {
    query += `&fetchAll=true`;
  }

  query = constructQueryUrl({ filterModel, query, columns });
  return await axios.get(query);
};

export const getReportsForSchedulerByDateRange = async ({ queryKey }) => {
  const [_, dateRange] = queryKey;
  const { from, to } = dateRange;
  let query =
    Constants.REPORTS_GET_SCHEDULER_DATA_FOR_DATE_RANGE +
    `?from=${from?.toISOString()}&to=${to?.toISOString()}`;
  return await axios.get(query);
};

export const getNavigationReport = async ({ queryKey }) => {
  const [
    _,
    pageOrObj,
    pageSize,
    field,
    sort,
    filterModel,
    columns,
    extraFilters,
    fetchAll,
  ] = queryKey;

  let page = 1;
  let take = 20;
  let sortby = field;
  let order = sort;
  let filters = filterModel;
  let cols = columns;
  let isFetchAll = fetchAll;
  let searchParams;

  if (typeof pageOrObj === "object" && pageOrObj !== null) {
    searchParams = pageOrObj.searchParams || pageOrObj;
    page = pageOrObj.page || 1;
    take = pageOrObj.pageSize || 20;
    sortby = pageOrObj.sortby;
    order = pageOrObj.order;
    filters = pageOrObj.filterModel;
  } else {
    page = pageOrObj || 1;
    take = pageSize || 20;
    searchParams = extraFilters;
  }

  let url = Constants.NAVIGATION_REPORT;
  if (searchParams?.type === "month" && searchParams?.params?.month && searchParams?.params?.year) {
    url += `?month=${searchParams.params.month}&year=${searchParams.params.year}`;
  } else if (searchParams?.type === "range" && searchParams?.params?.fromDate && searchParams?.params?.toDate) {
    url += `?fromDate=${searchParams.params.fromDate}&toDate=${searchParams.params.toDate}`;
  } else if (searchParams?.month && searchParams?.year) {
    url += `?month=${searchParams.month}&year=${searchParams.year}`;
  } else if (searchParams?.fromDate && searchParams?.toDate) {
    url += `?fromDate=${searchParams.fromDate}&toDate=${searchParams.toDate}`;
  } else {
    return { data: { res: [], meta: { itemCount: 0, pageCount: 0 } } };
  }

  url += `&page=${page}&take=${take}`;
  if (sortby) {
    url += `&sortby=${sortby}`;
  }
  if (order) {
    url += `&order=${order.toUpperCase()}`;
  }
  if (isFetchAll) {
    url += `&fetchAll=true`;
  }

  url = constructQueryUrl({ filterModel: filters, query: url, columns: cols });
  return await axios.get(url);
};

export const getBillingDeploymentReport = async ({ queryKey }) => {
  const [
    _,
    pageOrObj,
    pageSize,
    field,
    sort,
    filterModel,
    columns,
    extraFilters,
    fetchAll,
  ] = queryKey;

  let page = 1;
  let take = 20;
  let sortby = field;
  let order = sort;
  let filters = filterModel;
  let cols = columns;
  let isFetchAll = fetchAll;
  let status;
  let startMonth;
  let startYear;
  let endMonth;
  let endYear;

  if (typeof pageOrObj === "object" && pageOrObj !== null) {
    status = pageOrObj.status;
    startMonth = pageOrObj.startMonth;
    startYear = pageOrObj.startYear;
    endMonth = pageOrObj.endMonth;
    endYear = pageOrObj.endYear;
    page = pageOrObj.page || 1;
    take = pageOrObj.pageSize || 20;
    sortby = pageOrObj.sortby;
    order = pageOrObj.order;
    filters = pageOrObj.filterModel;
  } else {
    page = pageOrObj || 1;
    take = pageSize || 20;
    status = extraFilters?.status;
    startMonth = extraFilters?.startMonth;
    startYear = extraFilters?.startYear;
    endMonth = extraFilters?.endMonth;
    endYear = extraFilters?.endYear;
  }

  if (!status || !startMonth || !startYear || !endMonth || !endYear) {
    return { data: { res: [], meta: { itemCount: 0, pageCount: 0 } } };
  }

  let url = Constants.BILLING_DEPLOYMENT_REPORT.replace(":status", status)
    .replace(":startMonth", startMonth)
    .replace(":startYear", startYear)
    .replace(":endMonth", endMonth)
    .replace(":endYear", endYear);

  url += `&page=${page}&take=${take}`;
  if (sortby) {
    url += `&sortby=${sortby}`;
  }
  if (order) {
    url += `&order=${order.toUpperCase()}`;
  }
  if (isFetchAll) {
    url += `&fetchAll=true`;
  }

  url = constructQueryUrl({ filterModel: filters, query: url, columns: cols });
  return await axios.get(url);
};

export const schedulerProvisionAndScan = async () => {
  return await axios.post(Constants.ENTITLEMENT_SCHEDULER_PROVISIONING);
};

export const getListOfProvisioningCsv = async () => {
  return await axios.get(Constants.GET_PROVISIONING_CSV_LIST);
};

export const provisioningSelectedFiles = async (payload) => {
  return await axios.post(
    Constants.ENTITLEMENT_SCHEDULER_PROVISIONING,
    payload,
  );
};

export const getAppVersion = async () => {
  return await axios.get(Constants.SERVER_CONFIG);
};

export const getNavRenewalReport = async ({ queryKey }) => {
  const [
    _,
    pageOrObj,
    pageSize,
    field,
    sort,
    filterModel,
    columns,
    extraFilters,
    fetchAll,
  ] = queryKey;

  let page = 1;
  let take = 20;
  let sortby = field;
  let order = sort;
  let filters = filterModel;
  let cols = columns;
  let isFetchAll = fetchAll;
  let searchParams;

  if (typeof pageOrObj === "object" && pageOrObj !== null) {
    searchParams = pageOrObj.searchParams || pageOrObj;
    page = pageOrObj.page || 1;
    take = pageOrObj.pageSize || 20;
    sortby = pageOrObj.sortby;
    order = pageOrObj.order;
    filters = pageOrObj.filterModel;
  } else {
    page = pageOrObj || 1;
    take = pageSize || 20;
    searchParams = extraFilters;
  }

  const fromDate = searchParams?.fromDate;
  const toDate = searchParams?.toDate;

  if (!fromDate || !toDate) {
    return { data: { res: [], meta: { itemCount: 0, pageCount: 0 } } };
  }

  let url = `${Constants.NAV_RENEWAL_REPORT}?fromDate=${encodeURIComponent(new Date(fromDate).toISOString())}&toDate=${encodeURIComponent(new Date(toDate).toISOString())}`;

  url += `&page=${page}&take=${take}`;
  if (sortby) {
    url += `&sortby=${sortby}`;
  }
  if (order) {
    url += `&order=${order.toUpperCase()}`;
  }
  if (isFetchAll) {
    url += `&fetchAll=true`;
  }

  url = constructQueryUrl({ filterModel: filters, query: url, columns: cols });
  return await axios.get(url);
};